/**
 * Accounting.
 *
 * "Own ledger. Standalone product. No Noor dependency" is a decision ARCHITECTURE.md marks as
 * made and not to be relitigated. The ledger is real: apps/api/src/ledger.ts posts a balanced
 * double entry for every invoice and every payment, inside the same transaction that writes
 * them.
 *
 * The console can only show part of it. `GET /money/ledger/trial-balance` and
 * `POST /money/ledger/seed` are the only two ledger routes that exist. There is no route to
 * list journal entries, none to list the chart of accounts, and none for technician advances
 * and custody — even though `postJournal`, `postAdvance` and the `accounts`,
 * `journal_entries`, `journal_lines` and `technician_advances` tables are all built and
 * waiting. The missing pieces are routes.
 *
 * So this screen shows the trial balance honestly and names what it cannot show, rather than
 * rendering an empty "journal entries" table that would read as "the ledger is empty" when the
 * ledger is in fact full.
 */

import { Fragment, useMemo } from "react";
import { formatIQD } from "@fanni/shared/money";
import { ACCOUNT_KIND_AR } from "../lib/format.js";
import { useSeedChart, useTrialBalance } from "../lib/queries.js";
import {
  Badge,
  Button,
  Card,
  Empty,
  ErrorNote,
  Loading,
  Note,
  PageHeader,
  Stat,
  Table,
  Td,
  Th,
  Tr,
  cx,
} from "../ui/kit.js";

export function Accounting() {
  const { data, isLoading, error } = useTrialBalance();
  const seed = useSeedChart();

  const grouped = useMemo(() => {
    const rows = data?.rows ?? [];
    const map = new Map<string, typeof rows>();
    for (const r of rows) {
      const list = map.get(r.kind) ?? [];
      list.push(r);
      map.set(r.kind, list);
    }
    return [...map.entries()];
  }, [data]);

  const empty = (data?.rows.length ?? 0) === 0;

  return (
    <>
      <PageHeader
        title="المحاسبة"
        subtitle="دفتر فني الخاص. كل فاتورة وكل دفعة تُقيَّد قيداً مزدوجاً متوازناً في نفس المعاملة التي كتبتها."
        actions={
          empty ? (
            <Button variant="primary" size="sm" disabled={seed.isPending} onClick={() => seed.mutate(undefined)}>
              إنشاء دليل الحسابات الافتراضي
            </Button>
          ) : null
        }
      />

      {error ? <ErrorNote error={error} className="mb-4" /> : null}
      {seed.error ? <ErrorNote error={seed.error} className="mb-4" /> : null}

      {isLoading ? (
        <Loading />
      ) : (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-3">
            <Stat
              label="مجموع المدين"
              value={<span className="num">{formatIQD(data?.totals.debit_fils ?? 0)}</span>}
            />
            <Stat
              label="مجموع الدائن"
              value={<span className="num">{formatIQD(data?.totals.credit_fils ?? 0)}</span>}
            />
            <Stat
              label="الدفتر"
              value={data?.totals.balanced ? "متوازن" : "غير متوازن"}
              hint={
                data?.totals.balanced
                  ? "المدين يساوي الدائن — كما يجب"
                  : "فرق بين المدين والدائن. هذا خلل يستوجب المراجعة فوراً."
              }
              tone={data?.totals.balanced ? "good" : "bad"}
            />
          </div>

          <Card title="ميزان المراجعة" bodyClassName="">
            {empty ? (
              <Empty>لا توجد حسابات بعد. أنشئ دليل الحسابات الافتراضي للبدء.</Empty>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>الرمز</Th>
                    <Th>الحساب</Th>
                    <Th>النوع</Th>
                    <Th>مدين</Th>
                    <Th>دائن</Th>
                  </tr>
                </thead>
                <tbody>
                  {grouped.map(([kind, rows]) => (
                    <Fragment key={kind}>
                      <tr>
                        <Td className="bg-line-soft font-semibold text-ink-soft">
                          {ACCOUNT_KIND_AR[kind] ?? kind}
                        </Td>
                        <Td className="bg-line-soft" />
                        <Td className="bg-line-soft" />
                        <Td className="bg-line-soft" />
                        <Td className="bg-line-soft" />
                      </tr>
                      {rows.map((r) => (
                        <Tr key={r.code}>
                          <Td>
                            <span className="num text-ink-soft">{r.code}</span>
                          </Td>
                          <Td className="font-medium">{r.nameAr}</Td>
                          <Td>
                            <Badge tone="neutral">{ACCOUNT_KIND_AR[r.kind] ?? r.kind}</Badge>
                          </Td>
                          <Td>
                            <span className={cx("num", r.debitFils > 0 && "font-medium")}>
                              {r.debitFils > 0 ? r.debit_display : "—"}
                            </span>
                          </Td>
                          <Td>
                            <span className={cx("num", r.creditFils > 0 && "font-medium")}>
                              {r.creditFils > 0 ? r.credit_display : "—"}
                            </span>
                          </Td>
                        </Tr>
                      ))}
                    </Fragment>
                  ))}
                  <tr>
                    <Td className="bg-line-soft font-bold">الإجمالي</Td>
                    <Td className="bg-line-soft" />
                    <Td className="bg-line-soft" />
                    <Td className="bg-line-soft">
                      <span className="num font-bold">{formatIQD(data?.totals.debit_fils ?? 0)}</span>
                    </Td>
                    <Td className="bg-line-soft">
                      <span className="num font-bold">{formatIQD(data?.totals.credit_fils ?? 0)}</span>
                    </Td>
                  </tr>
                </tbody>
              </Table>
            )}
          </Card>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <Card title="القيود اليومية">
              <Note>
                غير متاحة: لا يوجد منفذ في الـ API يُرجِع القيود. الجداول
                <span className="num"> journal_entries </span>و<span className="num"> journal_lines </span>
                ممتلئة والدالة <span className="num">postJournal</span> تكتب فيها مع كل فاتورة ودفعة —
                الناقص هو المنفذ فقط، لا البيانات.
              </Note>
            </Card>

            <Card title="سُلف الفنيين والعُهدة">
              <Note>
                غير متاحة: جدول <span className="num">technician_advances</span> والدالة
                <span className="num"> postAdvance </span>
                جاهزان في الخادم، لكن لا يوجد منفذ لقراءتهما أو الكتابة فيهما. لا يمكن عرض سلفة نقدية
                ولا عهدة مواد من هنا اليوم.
              </Note>
            </Card>
          </div>
        </>
      )}
    </>
  );
}
